/**
 * Розділ «Бюджет» (етап Ф1 — затверджена версія як дані).
 *
 * Вкладки «Рік» і «Місяць» живі: читають версію бюджету з бекенду. «Точність» і
 * «Каса» лишаються з описом того, що там буде — вони залежать від шару факту
 * (Ф2) і від доступів до банку й бухгалтерії (Ф4).
 *
 * Операційні контроли (вкладка · місяць · сценарій) — у топбарі через
 * useBudgetUi (§6 дизайн-контракту), а не в тілі сторінки.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { pageMaxClass } from '@/utils/pageWidth';
import {
  Banknote, TrendingUp, Sun, Percent, CalendarDays, Table2, Clock, Receipt,
  Scale, SlidersHorizontal, Target, Wallet, ArrowLeftRight, FileClock,
  type LucideIcon,
} from 'lucide-react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { API_BASE_URL, authFetch } from '@/services/api';
import { useBudgetUi } from '@/hooks/useBudgetUi';
import type { YearResp, MonthResp, PlanFactResp, AccuracyResp, CashResp } from './types';
import { mln, n0, n1, n2, pct, SCENARIO_LABEL } from './types';
import { MonthlyTable, BreakevenTable, LoanTable, ParamsPanel } from './components/YearTables';
import { MonthSummary, HourlyProfile } from './components/MonthPanels';
import { PlanFactSection, AccuracySection } from './components/PlanFactPanels';
import VersionsSection, { type VersionRow } from './components/VersionsSection';
import CashSection from './components/CashSection';

const MONTHS = ['ak_bud_january', 'ak_bud_february', 'ak_bud_march', 'ak_bud_april', 'ak_bud_may_2', 'ak_bud_june', 'ak_bud_july', 'ak_bud_august', 'ak_bud_september', 'ak_bud_october', 'ak_bud_november', 'ak_bud_december'];
const monthLabel = (iso: string) => {
  const [y, m] = iso.split('-').map(Number);
  return m >= 1 && m <= 12 ? `${i18n.t(MONTHS[m - 1])} ${y}` : iso;
};

/**
 * Плитка показника за еталонною розкладкою кабінету (FinKpiRow на
 * «Фінансовому результаті»): іконка у квадраті ліворуч, далі колонка, що
 * займає всю решту ширини. Колір несе лише іконка (§5), тло нейтральне.
 */
function Kpi({ Icon, color, title, value, unit, note }: {
  Icon: LucideIcon; color: string; title: string;
  value: string; unit: string; note: string;
}) {
  const empty = value === '—';
  return (
    <div className="tile p-4 flex items-center gap-3.5">
      <div className="w-10 h-10 rounded-tile-sm flex items-center justify-center shrink-0"
        style={{ background: 'var(--surface-2)' }}>
        <Icon size={18} strokeWidth={1.75} style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] mb-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{title}</div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-display text-xl font-normal leading-none"
            style={{ color: empty ? 'var(--text-dim)' : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </span>
          <span className="text-[11px] shrink-0" style={{ color: 'var(--text-muted)' }}>{unit}</span>
        </div>
        {/* Фіксована висота рядка примітки — щоб плитки в ряду не різнились за
            висотою, коли в однієї примітка переноситься, а в іншої ні. */}
        <div className="mt-1 text-[10.5px] leading-[14px] truncate" title={note}
          style={{ color: 'var(--text-dim)' }}>{note}</div>
      </div>
    </div>
  );
}

function Section({ Icon, title, hint, children }: {
  Icon: LucideIcon; title: string; hint?: string; children: ReactNode;
}) {
  return (
    <div className="tile p-4">
      <div className="flex items-start justify-between gap-3 pb-3 mb-3"
        style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          <span className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--text)' }}>{title}</span>
        </div>
        {hint && <span className="text-[11px] shrink-0 text-right" style={{ color: 'var(--text-dim)' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Awaiting({ children }: { children: ReactNode }) {
  return (
    <div className="text-[12.5px] leading-relaxed py-6 text-center"
      style={{ color: 'var(--text-muted)' }}>{children}</div>
  );
}

export default function BudgetPage() {
  const { t } = useTranslation();
  const { tab, month, scenario, reloadToken, reload } = useBudgetUi();
  const { selectedAsset } = useSelectedAsset();
  const assetId = selectedAsset?.id ?? '';

  const [year, setYear] = useState<YearResp | null>(null);
  const [mon, setMon] = useState<MonthResp | null>(null);
  const [pf, setPf] = useState<PlanFactResp | null>(null);
  const [acc, setAcc] = useState<AccuracyResp | null>(null);
  const [vers, setVers] = useState<VersionRow[]>([]);
  const [cash, setCash] = useState<CashResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Індикатор лише на ПЕРШОМУ завантаженні (§9): далі свіжі дані підміняють
  // старі без блимання.
  const firstLoad = useRef(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!assetId) return;
    let dead = false;
    if (firstLoad.current) setLoading(true);
    setErr(null);
    (async () => {
      try {
        const q = `assetId=${encodeURIComponent(assetId)}`;
        const [yr, mr, pr, ar, vr, cr] = await Promise.all([
          authFetch(`${API_BASE_URL}/budget/year?${q}`),
          authFetch(`${API_BASE_URL}/budget/month?${q}&month=${month}`),
          authFetch(`${API_BASE_URL}/budget/plan-fact?${q}&month=${month}`),
          authFetch(`${API_BASE_URL}/budget/accuracy?${q}&month=${month}`),
          authFetch(`${API_BASE_URL}/budget/versions?${q}`),
          authFetch(`${API_BASE_URL}/budget/cash?${q}&month=${month}`),
        ]);
        if (!yr.ok) throw new Error(t('ak_bud_year_http', { s: yr.status }));
        if (!mr.ok) throw new Error(t('ak_bud_month_http', { s: mr.status }));
        const [y, m] = await Promise.all([yr.json(), mr.json()]);
        // План-факт і точність не критичні для сторінки: якщо не віддались,
        // решта вкладок має працювати, а не падати разом із ними.
        const p = pr.ok ? await pr.json() : null;
        const a = ar.ok ? await ar.json() : null;
        const vs = vr.ok ? await vr.json() : null;
        const cs = cr.ok ? await cr.json() : null;
        if (dead) return;
        setYear(y); setMon(m); setPf(p); setAcc(a); setCash(cs);
        setVers(vs && Array.isArray(vs.versions) ? vs.versions : []);
      } catch (e) {
        if (!dead) setErr(e instanceof Error ? e.message : t('ak_bud_failed_to_load'));
      } finally {
        if (!dead) { setLoading(false); firstLoad.current = false; }
      }
    })();
    return () => { dead = true; };
  }, [assetId, month, reloadToken]);

  const v = year?.version ?? null;
  const pl = mon?.pl ?? null;
  const yt = year?.year ?? null;
  // Сценарій у шапці може не збігатися з тим, який є в базі: помісячну
  // розкладку імпортовано лише для базового. Кажемо це прямо, а не підсовуємо
  // базові числа під чужим підписом.
  const scenarioMismatch = !!v && v.scenario !== scenario;

  const sub = [
    selectedAsset?.assetName || t('ak_bud_no_station_selected'),
    tab === 'year' ? (v ? t('ak_bud_budget_year', { p: v.periodLabel }) : t('ak_bud_budget_year_2')) : monthLabel(month),
    t('ak_bud_price_scenario', { s: SCENARIO_LABEL[scenario] }),
  ].join(' · ');

  // Плитки показників однакові за складом, значення — за активною вкладкою.
  const onYear = tab === 'year';
  const src = onYear ? yt : pl;
  const kpiRevenue = src?.revenue ?? null;
  const kpiBess = src?.bessEffect ?? null;
  const kpiEbitda = src?.ebitda ?? null;
  const kpiExport = src?.exportMwh ?? null;
  const kpiPrice = src?.avgPrice ?? null;
  const kpiDebt = onYear ? (yt?.debtService ?? null) : (pl ? (pl.finance ?? 0) + (pl.principal ?? 0) : null);
  const dscrYear = year?.dscr?.find((d) => d.period === 'Год')?.dscr ?? null;
  const stLabel = v?.status === 'approved' ? t('ak_bud_approved') : v?.status;
  const srcLabel = v?.source === 'xlsx_import' ? t('ak_bud_reference_form') : v?.source;

  return (
    <DashboardLayout breadcrumb={[t('ak_bud_budget')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className={`px-4 md:px-6 pt-5 pb-10 ${pageMaxClass(1440)}`}
          style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          <div className="mb-5">
            <h1 className="font-display text-2xl font-semibold leading-tight"
              style={{ color: 'var(--text)' }}>{t('ak_bud_budget')}</h1>
            <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>
          </div>

          {err && (
            <div className="tile p-4 mb-3 text-[12.5px]" style={{ color: 'var(--err)' }}>
              {t('ak_bud_failed_to_load_the_budget', { err })}
            </div>
          )}

          {loading && !year && (
            <div className="tile p-4 mb-3 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
              {t('ak_bud_loading')}
            </div>
          )}

          {!loading && year && !v && (
            <div className="tile p-4 mb-3 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
              {t('ak_bud_there_is_no_approved_budget_version_fo')}
            </div>
          )}

          {v && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
                <Kpi Icon={Banknote} color="var(--ok)" title={t('ak_bud_revenue')} unit={t('ak_bud_uah_mn')}
                  value={mln(kpiRevenue, 1)}
                  note={!onYear && pf
                    ? t('ak_bud_actual_mn_days', { r: mln(pf.mtd.factRevenue, 1), d: pf.mtd.daysWithFact })
                    : kpiBess ? t('ak_bud_incl_bess_mn', { v: mln(kpiBess, 1) }) : t('ak_bud_plan')} />
                <Kpi Icon={TrendingUp} color="var(--ok)" title="EBITDA" unit={t('ak_bud_uah_mn')}
                  value={mln(kpiEbitda, 1)}
                  note={kpiRevenue && kpiEbitda ? t('ak_bud_margin', { v: pct(kpiEbitda / kpiRevenue) }) : t('ak_bud_plan')} />
                <Kpi Icon={Sun} color="var(--ok)" title={t('ak_bud_grid_export')} unit={t('ak_bud_mwh')}
                  value={n0(kpiExport)}
                  note={!onYear && pf
                    ? t('ak_bud_actual_mwh', { v: n1(pf.mtd.factKwh / 1000) })
                    : kpiPrice ? t('ak_bud_avg_price_uah_mwh', { v: n0(kpiPrice) }) : t('ak_bud_plan')} />
                {onYear ? (
                  <Kpi Icon={Percent} color="var(--series-2)" title={t('ak_bud_debt_service')} unit={t('ak_bud_uah_mn')}
                    value={mln(kpiDebt, 1)}
                    note={dscrYear ? `DSCR ${n2(dscrYear)}` : t('ak_bud_interest_fees_principal')} />
                ) : (
                  <Kpi Icon={Target} color="var(--series-3)" title={t('ak_bud_month_forecast')} unit={t('ak_bud_uah_mn')}
                    value={pf?.forecast ? mln(pf.forecast.revenue, 1) : '—'}
                    note={pf?.forecast?.deltaToBudget != null
                      ? t('ak_bud_vs_budget', { v: (pf.forecast.deltaToBudget > 0 ? '+' : '') + pct(pf.forecast.deltaToBudget) })
                      : t('ak_bud_actual_plan_for_the_remaining_days')} />
                )}
              </div>

              {scenarioMismatch && (
                <div className="tile p-3 mb-3 text-[11.5px]" style={{ color: 'var(--warn)' }}>
                  {t('ak_bud_the_monthly_breakdown_has_been_entered')}
                </div>
              )}
            </>
          )}

          {tab === 'month' && v && (
            <div className="grid grid-cols-1 gap-3">
              <VersionsSection versions={vers} month={month} onChanged={reload} />

              <Section Icon={CalendarDays} title={t('ak_bud_month_summary')}
                hint={t('ak_bud_version', { st: stLabel, src: srcLabel })}>
                <MonthSummary pl={pl} plFrom={mon?.plFrom ?? 'year'} />
              </Section>

              {/* Графік і таблиця — власна секція за еталоном «Планового доходу»:
                  заголовок поза плиткою, графік у своїй, таблиця у своїй. Інакше
                  вийшла б плитка в плитці (§3). */}
              {mon ? (
                <HourlyProfile hours={mon.hours} has={mon.has} monthLabel={monthLabel(month)}
                  planMonthMwh={pl?.exportMwh ?? null} planMonthRevenue={pl?.revenue ?? null} />
              ) : (
                <Section Icon={Clock} title={t('ak_bud_daily_profile_prices_and_release_plan')}>
                  <Awaiting>{t('ak_bud_loading')}</Awaiting>
                </Section>
              )}

              {pf ? <PlanFactSection data={pf} /> : (
                <Section Icon={Table2} title={t('ak_bud_plan_vs_actual_by_day')}>
                  <Awaiting>{t('ak_bud_loading')}</Awaiting>
                </Section>
              )}

              <Section Icon={Receipt} title={t('ak_bud_costs_accrued_and_paid')}
                hint={t('ak_bud_requires_access_to_accounting')}>
                <Awaiting>
                  {t('ak_bud_fixed_directory_items_against_actual_a')}
                </Awaiting>
              </Section>
            </div>
          )}

          {tab === 'year' && v && year && (
            <div className="grid grid-cols-1 gap-3">
              <Section Icon={Table2} title={t('ak_bud_by_month')}
                hint={t('ak_bud_uah_excl_vat', { f: v.sourceFile ? v.sourceFile : v.source })}>
                <MonthlyTable months={year.months} year={year.year} />
              </Section>

              <Section Icon={Scale} title={t('ak_bud_break_even_and_dscr')}
                hint={t('ak_bud_fixed_costs_1_5_5_variable')}>
                <BreakevenTable months={year.months} dscr={year.dscr} />
              </Section>

              <Section Icon={Percent} title={t('ak_bud_loan')}
                hint={v.note ? t('ak_bud_see_the_version_note_below') : undefined}>
                <LoanTable loan={year.loan} />
                {year.sourceNote && (
                  <div className="text-[11px] mt-3 pt-3" style={{ color: 'var(--warn)', borderTop: '1px solid var(--line)' }}>
                    {year.sourceNote}
                  </div>
                )}
              </Section>

              <Section Icon={SlidersHorizontal} title={t('ak_bud_parameters')}
                hint={t('ak_bud_approved_2', { by: v.approvedBy ? v.approvedBy : '—' })}>
                <ParamsPanel params={year.params} opex={year.opex} byScenario={year.yearByScenario} />
              </Section>
            </div>
          )}

          {tab === 'accuracy' && (
            acc ? <AccuracySection data={acc} /> : (
              <div className="tile p-4 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
                {t('ak_bud_loading')}
              </div>
            )
          )}

          {tab === 'cash' && (
            cash ? (
              <CashSection data={cash} assetId={assetId} month={month} onChanged={reload} />
            ) : (
              <Section Icon={Wallet} title={t('ak_bud_cash')}>
                <Awaiting>
                  {t('ak_bud_the_payment_calendar_is_calculated_fro')}
                </Awaiting>
              </Section>
            )
          )}

        </div>
      </div>
    </DashboardLayout>
  );
}
